import * as React from 'react';
import { useForkRef, useIsomorphicLayoutEffect, useEventCallback } from '@cian/react-utils';
import { useIsFocusVisible } from '../../../../../internal/hooks';
import { ownerDocument } from '../../../../../internal/utils';
import { valueToPercent } from '../../helpers';
import { useSliderValueThumbsKeyDownEvent } from './useSliderValueThumbsKeyDownEvent';
export function useSliderValueThumbs(_ref) {
  let {
    props,
    valueState,
    marks
  } = _ref;
  const {
    disabled,
    min,
    max
  } = props;
  const sliderRef = React.useRef(null);
  // We can't use the :active browser pseudo-classes.
  // - The active state isn't triggered when clicking on the rail.
  // - The active state isn't transferred when inversing a range slider.
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [openIndex, setOpenIndex] = React.useState(-1);
  const [focusVisibleIndex, setFocusVisibleIndex] = React.useState(-1);
  const {
    isFocusVisibleRef,
    onFocus: handleFocusVisible,
    onBlur: handleBlurVisible,
    ref: focusVisibleRef
  } = useIsFocusVisible();
  const handleSliderRef = useForkRef(sliderRef, focusVisibleRef);
  const handleKeyDown = useSliderValueThumbsKeyDownEvent({
    props,
    valueState,
    setFocusIndex,
    setFocusVisibleIndex,
    marks
  });
  const handleFocus = useEventCallback(event => {
    const index = Number(event.currentTarget.getAttribute('data-index'));
    handleFocusVisible(event);
    if (isFocusVisibleRef.current === true) {
      setFocusVisibleIndex(index);
    }
    setOpenIndex(index);
  });
  const handleBlur = useEventCallback(event => {
    handleBlurVisible(event);
    if (!isFocusVisibleRef.current) {
      setFocusVisibleIndex(-1);
    }
    setOpenIndex(-1);
  });
  const handleMouseOver = useEventCallback(event => {
    const index = Number(event.currentTarget.getAttribute('data-index'));
    setOpenIndex(index);
  });
  const handleMouseLeave = useEventCallback(() => {
    setOpenIndex(-1);
  });
  if (disabled && focusVisibleIndex !== -1) {
    if (focusVisibleIndex !== -1) {
      setFocusVisibleIndex(-1);
    }
    if (activeIndex !== -1) {
      setActiveIndex(-1);
    }
  }
  useIsomorphicLayoutEffect(() => {
    const slider = sliderRef.current;
    const {
      activeElement
    } = ownerDocument(slider);
    if (disabled && slider && activeElement && slider.contains(activeElement)) {
      // This is necessary because Firefox and Safari will keep focus
      // on a disabled element:
      // https://codesandbox.io/s/mui-pr-22247-forked-h151h?file=/src/App.js
      activeElement.blur();
    }
  }, []);
  function setFocusIndex(index) {
    const slider = sliderRef.current;
    const {
      activeElement
    } = ownerDocument(slider);
    if (slider) {
      if (!slider.contains(activeElement) || activeElement && Number(activeElement.getAttribute('data-index')) !== index) {
        const thumbElement = slider.querySelector("[role=\"slider\"][data-index=\"".concat(index, "\"]"));
        if (thumbElement) {
          thumbElement.focus();
        }
      }
    }
    setActiveIndex(index);
  }
  const thumbs = valueState.values.map((value, index) => {
    const active = activeIndex === index;
    const open = openIndex === index || active;
    const focusVisible = focusVisibleIndex === index;
    const percent = valueToPercent(value, min, max);
    return {
      open,
      active,
      focusVisible,
      value,
      percent,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onMouseOver: handleMouseOver,
      onMouseLeave: handleMouseLeave,
      onKeyDown: handleKeyDown
    };
  });
  return {
    sliderRef: handleSliderRef,
    setOpenIndex,
    setFocusIndex,
    setActiveIndex,
    thumbs
  };
}